// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { RuleResource, SubRule } from '@api/types';
import type { Decoded, Rule, TenantRule } from '../types';
import { beforeOtherwise, insertAt } from './order';
import { EXPR_TYPE_TRUE, readAction, recogniseCondition, synthesise, writeAction } from './wire';

// OTHERWISE is checked first so it wins over `capabilities.system`, and recognition before
// `capabilities.system` so an unrecognised system rule is opaque rather than conditionless.
const toRule = (served: SubRule, servedIndex: number): Rule | null => {
  const base = {
    key: `r${servedIndex}`,
    action: readAction(served),
    caps: served.capabilities ?? {},
    allowedValues: served.allowedValues ?? [],
  };
  if (served.exprType === EXPR_TYPE_TRUE) { return { ...base, kind: 'otherwise', served }; }

  const recognised = recogniseCondition(served);
  if (recognised === null) { return null; }

  const { condition, conditionValue } = recognised;
  return base.caps.system === true
    ? { ...base, kind: 'system', condition, served }
    : { ...base, kind: 'tenant', condition, conditionValue, served };
};

/** Total: every sub-rule lands in exactly one of `rules` or `opaque`. */
export const decode = (resource: RuleResource): Decoded => {
  const classified = resource.valueRule.subRules.map((served, servedIndex) => ({
    served,
    servedIndex,
    rule: toRule(served, servedIndex),
  }));

  return {
    rules: classified.flatMap(({ rule }) => (rule === null ? [] : [rule])),
    opaque: classified.flatMap(({ rule, served, servedIndex }) =>
      rule === null ? [{ served, servedIndex }] : [],
    ),
    version: resource.version,
  };
};

const conditionChanged = (rule: TenantRule, served: SubRule): boolean => {
  const recognised = recogniseCondition(served);
  return recognised?.condition !== rule.condition || recognised?.conditionValue !== rule.conditionValue;
};

const tenantSubRule = (rule: TenantRule): SubRule => {
  if (rule.served === null) {
    return synthesise(rule.condition, rule.conditionValue);
  }

  return conditionChanged(rule, rule.served)
    ? { ...rule.served, ...synthesise(rule.condition, rule.conditionValue) }
    : rule.served;
};

const toSubRule = (rule: Rule): SubRule =>
  writeAction(rule.kind === 'tenant' ? tenantSubRule(rule) : rule.served, rule.action);

/** Patches served sub-rules and splices the opaque ones back immediately before OTHERWISE. */
export const encode = (resource: RuleResource, edited: Decoded): RuleResource => ({
  ...resource,
  version: edited.version,
  valueRule: {
    ...resource.valueRule,
    subRules: insertAt(
      edited.rules.map(toSubRule),
      beforeOtherwise(edited.rules),
      ...edited.opaque.map(({ served }) => served),
    ),
  },
});
